import { z } from 'zod';
import type { DiscoveryReviewStatus, DiscoverySearchStatus, OperatingStatus } from '../constants';
import { DISCOVERY_REVIEW_STATUSES } from '../constants';
import type { WebsiteLookupDTO } from './website';

const boolish = z.preprocess((v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : v), z.boolean());

/**
 * Setup instructions per built-in provider (shown when a provider is not configured). Terms/attribution
 * come from the provider itself (GET /api/discovery/providers).
 */
export const DISCOVERY_PROVIDER_SETUP: Record<string, string> = {
  osm:
    'Add one line to the server .env file (.env at the repository root, or your deployment\'s environment) — ' +
    'OSM_CONTACT_EMAIL=sales-ops@yourcompany.com, using a monitored address of your own — then restart the API and the ' +
    'worker. No account, key or payment is involved: the OpenStreetMap Nominatim and Overpass usage policies simply ' +
    'require every request to identify the application and a contact address. Optional: NOMINATIM_URL / OVERPASS_URL ' +
    'to point at your own or a commercial instance.',
  google_places:
    'Create a Google Cloud project, enable "Places API (New)", create an API key restricted to that API, then set ' +
    'GOOGLE_PLACES_API_KEY and restart the API and worker. Text Search requests are billed by Google per request.',
};

/**
 * How many branches the operator runs — the shape of the lead, not the cuisine. Counts come from the branches
 * the search actually found in its area, which is what you can act on from that search; a chain's branches
 * elsewhere are not visible from here.
 */
export const DISCOVERY_LOCATION_FILTERS = ['any', 'single', 'small', 'mid', 'chain'] as const;
export type DiscoveryLocationFilter = (typeof DISCOVERY_LOCATION_FILTERS)[number];

export const DISCOVERY_LOCATION_LABELS: Record<DiscoveryLocationFilter, string> = {
  any: 'Any size',
  single: 'Single location',
  small: 'Small chain (2–5 branches)',
  mid: 'Larger chain (6–25 branches)',
  chain: 'Chain, 2–25 branches (not one shop, not a giant)',
};

/** Branch counts behind each size filter, inclusive. `any` has no bounds. */
export const DISCOVERY_LOCATION_RANGES: Record<DiscoveryLocationFilter, { min: number; max: number | null }> = {
  any: { min: 0, max: null },
  single: { min: 0, max: 1 },
  small: { min: 2, max: 5 },
  mid: { min: 6, max: 25 },
  chain: { min: 2, max: 25 },
};

/**
 * "What" presets for the search form: a pizza-specific query, optional keyword refinement, and the operator
 * size to look for. `chain` presets make the provider prefer places tagged as part of a brand.
 */
export const DISCOVERY_QUERY_PRESETS = [
  { key: 'pizza_restaurants', label: 'Pizza restaurants', query: 'pizza restaurant', keywords: '', locations: 'any' },
  { key: 'pizzerias', label: 'Pizzerias', query: 'pizzeria', keywords: '', locations: 'any' },
  { key: 'wood_fired', label: 'Wood-fired pizza', query: 'wood-fired pizza', keywords: 'wood-fired, wood fired, wood oven, four à bois', locations: 'any' },
  { key: 'neapolitan', label: 'Neapolitan pizza', query: 'neapolitan pizza', keywords: 'neapolitan, napoletana, napolitaine, napoli', locations: 'any' },
  { key: 'slice', label: 'Pizza by the slice', query: 'pizza by the slice', keywords: 'slice, slices, pointe', locations: 'any' },
  { key: 'larger_chains', label: 'Larger pizza chains (2–25 locations)', query: 'pizza', keywords: '', locations: 'chain' },
  { key: 'small_groups', label: 'Small groups / growing operators (2–5)', query: 'pizza', keywords: '', locations: 'small' },
  { key: 'big_groups', label: 'Bigger chains (6–25)', query: 'pizza', keywords: '', locations: 'mid' },
  { key: 'independents', label: 'One-shop pizzerias', query: 'pizzeria', keywords: '', locations: 'single' },
] as const satisfies readonly { key: string; label: string; query: string; keywords: string; locations: DiscoveryLocationFilter }[];

export const DISCOVERY_MATCH_FILTERS = ['new', 'in_crm', 'possible_duplicate'] as const;
export type DiscoveryMatchFilter = (typeof DISCOVERY_MATCH_FILTERS)[number];

/** POST /api/discovery/searches */
export const CreateDiscoverySearchBody = z
  .object({
    provider: z.string().trim().min(1).max(60),
    query: z.string().trim().max(200).default('pizza').transform((v) => (v === '' ? 'pizza' : v)),
    locationText: z.string().trim().max(200).nullish().transform((v) => (v ? v : null)),
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    radiusKm: z.number().min(1).max(100).default(25),
    keywords: z.string().trim().max(500).nullish().transform((v) => (v ? v : null)),
    /** Operator size to look for; the results and the review queue are limited to it. */
    locations: z.enum(DISCOVERY_LOCATION_FILTERS).default('any'),
    limit: z.number().int().min(1).max(200).default(100),
  })
  .refine((b) => (b.latitude == null) === (b.longitude == null), {
    message: 'Give both latitude and longitude, or neither',
    path: ['longitude'],
  })
  .refine((b) => b.locationText !== null || (b.latitude != null && b.longitude != null), {
    message: 'Enter a city or region (e.g. "Montréal, QC")',
    path: ['locationText'],
  });
export type CreateDiscoverySearchBody = z.input<typeof CreateDiscoverySearchBody>;

/** GET /api/discovery/searches/:id query */
export const DiscoveryResultsQuery = z.object({
  status: z.enum(DISCOVERY_REVIEW_STATUSES).optional(),
  match: z.enum(DISCOVERY_MATCH_FILTERS).optional(),
  locations: z.enum(DISCOVERY_LOCATION_FILTERS).optional(),
  minRating: z.coerce.number().min(0).max(5).optional(),
  newlyOpened: boolish.optional(),
  hidePreviouslyRejected: boolish.optional(),
  /** Only results with an email — from the source or found on their website. */
  hasEmail: boolish.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type DiscoveryResultsQuery = z.input<typeof DiscoveryResultsQuery>;

const TagIds = z.array(z.uuid()).max(50);

export const ImportDiscoveryResultBody = z.object({
  ownerId: z.uuid().nullish(),
  tagIds: TagIds.optional(),
  stageKey: z.string().trim().min(1).max(60).optional(),
});
export type ImportDiscoveryResultBody = z.infer<typeof ImportDiscoveryResultBody>;

export const MergeDiscoveryResultBody = z.object({ businessId: z.uuid() });
export type MergeDiscoveryResultBody = z.infer<typeof MergeDiscoveryResultBody>;

export const RejectDiscoveryResultBody = z.object({
  reason: z.string().trim().max(500).nullish().transform((v) => (v ? v : null)),
});
export type RejectDiscoveryResultBody = z.input<typeof RejectDiscoveryResultBody>;

export const BulkDiscoveryReviewBody = z.object({
  ids: z.array(z.uuid()).min(1).max(200),
  action: z.enum(['import', 'reject']),
  ownerId: z.uuid().nullish(),
  tagIds: TagIds.optional(),
  stageKey: z.string().trim().min(1).max(60).optional(),
  reason: z.string().trim().max(500).nullish(),
});
export type BulkDiscoveryReviewBody = z.infer<typeof BulkDiscoveryReviewBody>;

export type DiscoveryProviderDTO = {
  key: string;
  label: string;
  configured: boolean;
  terms: string;
  /** How to configure the provider (null for providers without built-in instructions). */
  setup: string | null;
};

export type DiscoverySearchDTO = {
  id: string;
  provider: string;
  providerLabel: string;
  query: string;
  locationText: string | null;
  latitude: number | null;
  longitude: number | null;
  radiusKm: number | null;
  keywords: string | null;
  /** Operator size this search looked for. */
  locations: DiscoveryLocationFilter;
  status: DiscoverySearchStatus;
  resultCount: number;
  newCount: number;
  existingCount: number;
  /** Results still awaiting review. */
  pendingCount: number;
  /** Why the search failed (status failed only). */
  error: string | null;
  /** Non-fatal notes: "retrying after a rate limit", "result limit reached"… */
  warning: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

export type DiscoveryMatchKind = 'new' | 'in_crm' | 'possible_duplicate';

export type DiscoveryResultDTO = {
  id: string;
  searchId: string;
  provider: string;
  externalId: string;
  name: string;
  /** Chain/franchise name from the source, when the place is part of one. */
  brand: string | null;
  /** Operating company, when the source distinguishes it from the brand. */
  operator: string | null;
  /** Branches of this brand found by this search (1 when it is not part of one). */
  branchCount: number;
  addressLine1: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  website: string | null;
  /** The email the source listing gives (OpenStreetMap tags; Google Places has none). */
  email: string | null;
  /**
   * Reading the place's own website for its published email, when the source gave none; null when there is no
   * website or the source already had an email. Importing or merging uses this address when `email` is empty.
   */
  websiteLookup: WebsiteLookupDTO | null;
  category: string | null;
  cuisine: string | null;
  rating: number | null;
  ratingCount: number | null;
  operatingStatus: OperatingStatus;
  openedOn: string | null;
  sourceUrl: string | null;
  attribution: string | null;
  matchKind: DiscoveryMatchKind;
  matchConfidence: number | null;
  matchSignals: { signal: string; points: number; detail: string }[];
  /** The matched CRM lead; null when there is no match or the lead is not visible to the user. */
  matchedBusiness: { id: string; name: string; stageId: string; ownerId: string | null; city: string | null } | null;
  previouslyRejected: number;
  reviewStatus: DiscoveryReviewStatus;
  reviewedAt: string | null;
  businessId: string | null;
  /** Estimated fit score from what the provider tells us (no engagement data yet). */
  estimatedScore: { score: number; tierKey: string; reasons: string[] };
};

export type DiscoverySearchDetailDTO = {
  search: DiscoverySearchDTO;
  results: { items: DiscoveryResultDTO[]; total: number; page: number; pageSize: number };
  /** Counts over all results of the search (ignoring filters) for filter chips. */
  summary: {
    pending: number;
    imported: number;
    merged: number;
    rejected: number;
    new: number;
    inCrm: number;
    possibleDuplicate: number;
    /** Results with an email, from the source or their website. */
    withEmail: number;
    /** Websites still waiting to be read. */
    websitesPending: number;
  };
};

export type DiscoveryReviewOutcomeDTO = {
  id: string;
  ok: boolean;
  reviewStatus: DiscoveryReviewStatus;
  businessId: string | null;
  error: string | null;
};

export type DiscoveryImportResponse = { businessId: string; warnings: string[]; duplicatesFlagged: number };
export type DiscoveryMergeResponse = { businessId: string; filled: string[]; proposed: string[] };
export type DiscoveryBulkResponse = { results: DiscoveryReviewOutcomeDTO[]; succeeded: number; skipped: number; failed: number };
